import React, { useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import Form from "../../components/Form";
import PropTypes from "prop-types";
import Paper from "../../components/Paper";
import { makeStyles } from "../../utils/styles";
import Typography from "../../components/Typography";
import Divider from "../../components/Divider";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { FORM } from "../../utils/constans";
import Button from "../../components/Button";
import ButtonGroup from "../../components/ButtonGroup";
import Icon from "../../components/Icon";
import useTheme from "../../hooks/useTheme";
import { Col, Row } from "react-styled-flexboxgrid";
import Input from "../../components/Input";
import Map from "../../components/Map";
import ImageUpload from "../../components/ImageUpload";
import TextArea from "../../components/TextArea";
import { parseUserSuppliersToOptions, preventDefault } from "../../utils/helpers";
import Box from "../../components/Box";
import Loader from "../../components/Loader";
import Select from "../../components/Select";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((css) => ({
    paper: css`
        position: relative;
        overflow: hidden;
        padding: 30px 30px 15px 30px;
        width: 940px;
    `,
    dividerHeader: css`
        margin-top: 25px;
        margin-bottom: 30px;
    `,
    dividerFooter: css`
        margin-top: 30px;
        margin-bottom: 15px;
    `,
    buttonsFooter: css`
        justify-content: flex-end;
    `,
    boxLeft: css`
        margin-right: 40px;
    `,
    boxRight: css`
        margin-left: 40px;
    `,
}));

const schema = yup.object().shape({
    name: yup.string().required(FORM.FIELD_IS_REQUIRED),
    description: yup.string().required(FORM.FIELD_IS_REQUIRED),
    url: yup.string().required(FORM.FIELD_IS_REQUIRED),
    location: yup.object().required(FORM.FIELD_IS_REQUIRED),
    logo: yup.mixed().required(FORM.FIELD_IS_REQUIRED),
    supplier_id: yup.string().required(FORM.FIELD_IS_REQUIRED),
});

const getPreloadedValues = () => ({
    supplier_id: ''
});

const FormPlace = ({ loading, onSubmit, onCancel, initialValues, edit, availableAdmins }) => {
    const { t } = useTranslation()
    const styles = useStyles();
    const theme = useTheme();
    const { register, handleSubmit, errors, setValue, watch, control } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            ...getPreloadedValues(),
            ...initialValues
        },
    });

    useEffect(() => {
        register('logo');
        register('location');
    }, [register]);

    const adminsOptions = useMemo(() => {
        return parseUserSuppliersToOptions(availableAdmins);
    }, [availableAdmins]);

    return (
        <Paper
            customStyle={styles.paper}
        >
            <Form onSubmit={preventDefault}>
                <Typography as="h1">
                    {edit ? t("Edytuj obiekt") : t("Dodaj obiekt")}
                </Typography>
                <Divider customStyle={styles.dividerHeader} />
                <Row>
                    <Col xs={6}>
                        <Box customStyle={styles.boxLeft}>
                            <Input
                                type="text"
                                ref={register}
                                name="name"
                                label={t("Nazwa obiektu")}
                                placeholder={t("Nazwa obiektu")}
                                error={!!errors.name}
                                errorText={errors.name?.message}
                            />
                            <Map
                                name="location"
                                currentLocation={watch('location')}
                                onFindPlace={setValue}
                                error={!!errors.location}
                                errorText={errors.location?.message}
                            />
                        </Box>
                    </Col>
                    <Col xs={6}>
                        <Box customStyle={styles.boxRight}>
                            <ImageUpload
                                label={t("Logo")}
                                name="logo"
                                image={watch('logo')}
                                onDrop={setValue}
                                error={!!errors.logo}
                                errorText={errors.logo?.message}
                            />
                            <TextArea
                                ref={register}
                                name="description"
                                label={t("Opis")}
                                placeholder={t("Opis")}
                                error={!!errors.description}
                                errorText={errors.description?.message}
                                fullWidth
                                height={94}
                            />
                            <Input
                                type="text"
                                ref={register}
                                name="url"
                                label={t("Adres www")}
                                placeholder={t("Wpisz adres strony internetowej")}
                                error={!!errors.url}
                                errorText={errors.url?.message}
                            />
                            <Controller
                                control={control}
                                name="supplier_id"
                                render={({ onChange, value }) => (
                                    <Select
                                        type="text"
                                        name="supplier_id"
                                        value={value}
                                        onChange={onChange}
                                        label={t("Administrator obiektu")}
                                        placeholder={t("Administrator obiektu")}
                                        options={adminsOptions}
                                        error={!!errors.supplier_id}
                                        errorText={errors.supplier_id?.message}
                                        fullWidth
                                    />
                                )}
                            />
                        </Box>
                    </Col>
                </Row>
                <Divider customStyle={styles.dividerFooter} />
                <ButtonGroup customStyle={styles.buttonsFooter}>
                    <Button
                        variant="text"
                        onClick={onCancel}
                        startIcon={
                            <Icon
                                name="close"
                                width={12}
                                height={12}
                                fill={theme.colors.purple}
                            />
                        }
                    >
                        {t("Anuluj")}
                    </Button>
                    <Button
                        onClick={handleSubmit(onSubmit)}
                        startIcon={!edit ?
                            <Icon name="add" width={12} height={12} fill={theme.colors.white} /> :
                            <Icon name="save" width={14} height={14} fill={theme.colors.white} />
                        }
                        disabled={loading}
                    >
                        {edit ? t("Zapisz") : t("Dodaj obiekt")}
                    </Button>
                </ButtonGroup>
            </Form>
            <Loader show={loading} />
        </Paper>
    );
};

FormPlace.propTypes = {
    loading: PropTypes.bool,
    onSubmit: PropTypes.func,
    onCancel: PropTypes.func,
    edit: PropTypes.bool,
    initialValues: PropTypes.object,
    availableAdmins: PropTypes.array,
};

export default FormPlace;
